import React, {useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {
  getNumberOfRooms,
  getShowTotalPrices
} from 'modules/sapiSearch/selectors'

import {Button} from '@vio/atlas/Button'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Card} from '@vio/atlas/src/components/designSystem/Card'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {Offer} from '@vio/core/src/offer/types/offer'
import {formatPriceValue} from '@vio/core/src/price/business/formatPrice'
import {getRoomOfferPrices} from '@vio/core/src/price/business/roomOfferPrices'
import {getRoundingStrategy} from '@vio/core/src/utils/number'
import {usePreviousValue} from '@vio/core/src/utils/react/hooks/usePreviousValue'

interface Props {
  nextAvailableOffer?: Offer
  remainingCount: number
  onClick: () => void
  isExpanded: boolean
}

const ButtonWrapper = styled(Button)`
  border-top: 1px solid ${cssTheme.colors.border.neutral.secondary};
`

const useGetNextAvailableOfferPrice = ({
  nextAvailableOffer
}: {
  nextAvailableOffer?: Offer
}) => {
  const {locale} = useIntl()
  const hotelId = useSelector(getAccommodationPageHotelId)
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const showTotalPrices = useSelector(getShowTotalPrices)

  const prices = getRoomOfferPrices(nextAvailableOffer?.prices, {
    numberOfRooms
  })

  if (!prices) {
    return {
      mainPriceValue: 0,
      currencyCode: '',
      locale: ''
    }
  }

  return {
    mainPriceValue: showTotalPrices ? prices.total.price : prices.nightly.price,
    currencyCode: prices.currencyCode,
    locale
  }
}

const SCROLL_OFFSET_DESKTOP = 100
const SCROLL_OFFSET_MOBILE = 50

export const ExpandableRoomOffersToggle = ({
  nextAvailableOffer,
  remainingCount,
  onClick,
  isExpanded
}: Props) => {
  const {isMobile} = useDeviceLayout()
  const scrollRef = useRef<HTMLDivElement>(null)

  const previousIsExpanded = usePreviousValue(isExpanded)

  useEffect(() => {
    if (previousIsExpanded && !isExpanded && scrollRef.current) {
      const {bottom} = scrollRef.current.getBoundingClientRect()
      window.scrollTo({
        top:
          window.scrollY +
          bottom +
          (isMobile ? SCROLL_OFFSET_MOBILE : SCROLL_OFFSET_DESKTOP) -
          window.innerHeight,
        behavior: 'smooth'
      })
    }
  }, [previousIsExpanded, isExpanded])

  const {mainPriceValue, currencyCode, locale} = useGetNextAvailableOfferPrice({
    nextAvailableOffer
  })

  return (
    <div ref={scrollRef}>
      {' '}
      {!isMobile ? (
        <ButtonWrapper
          variant="ghostInverse"
          fullWidth
          onClick={onClick}
          iconEnd={
            <Icon
              colorPath="content.neutral.primary"
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            />
          }
        >
          <Text colorPath="content.neutral.primary" variant="labelS">
            {isExpanded ? (
              <FormattedMessageWrapper
                id="showLessButton"
                defaultMessage="Show less"
              />
            ) : (
              <FormattedMessageWrapper
                id="moreDealsButton"
                defaultMessage="Show {remainingCount, plural, one {1 more deal} other {# more deals}}"
                values={{
                  remainingCount
                }}
              />
            )}
          </Text>
        </ButtonWrapper>
      ) : mainPriceValue ? (
        <Box margin="s400" marginTop="s300" marginBottom="s000">
          <Card borderColorPath="border.neutral.secondary" borderRadius="lg">
            <Button
              fullWidth
              variant="ghost"
              onClick={onClick}
              iconEnd={
                <Icon
                  colorPath="content.neutral.primary"
                  name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                />
              }
            >
              {isExpanded ? (
                <FormattedMessageWrapper
                  id="showLessButton"
                  defaultMessage="Show less"
                />
              ) : (
                <FormattedMessageWrapper
                  id="showMoreFromButtonMobile"
                  defaultMessage="Show more from {price}"
                  values={{
                    price: formatPriceValue({
                      locale: locale,
                      value: mainPriceValue,
                      currency: currencyCode,
                      roundingStrategy: getRoundingStrategy(),
                      maximumFractionDigits: 0
                    })
                  }}
                />
              )}
            </Button>
          </Card>
        </Box>
      ) : null}
    </div>
  )
}
